import { useRef, useState } from "react";
import type { DataSource } from "../data/source";
import type { CleanseResult } from "../lib/api";
import {
  buildApiHeaders,
  mapResultToRow,
  mapErrorToRow,
  type ProcessingMode,
} from "../lib/cleanseSchema";
import type { RecoverySnapshot } from "../lib/recovery";

const BATCH_SIZE = 5;
const UPRN_BATCH_SIZE = 2;
const PAUSE_POLL_MS = 150;

export interface ProcessingInput {
  rows: Record<string, any>[];
  csvHeaders: string[];
  mode: ProcessingMode;
  line1Col: string;
  postcodeCol: string;
  uprnCol: string;
}

export interface UseProcessingOptions {
  dataSource: DataSource;
  setError: (msg: string | null) => void;
  // Persist a snapshot of in-progress work after each completed batch and on
  // credit-exhaustion pauses. The composer wires this to localStorage.
  saveSnapshot: (snapshot: Omit<RecoverySnapshot, "timestamp">) => void;
  // Clear the snapshot on successful (non-cancelled) completion.
  clearSnapshot: () => void;
}

export function useProcessing(opts: UseProcessingOptions) {
  const { dataSource, setError, saveSnapshot, clearSnapshot } = opts;

  const [processing, setProcessing] = useState(false);
  const [paused, setPaused] = useState(false);
  // Refs mirror processing-control state so the async loop reads the latest
  // value rather than a stale closure capture.
  const pausedRef = useRef(false);
  const cancelledRef = useRef(false);

  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [resultRows, setResultRows] = useState<any[]>([]);

  async function start(
    input: ProcessingInput,
    resumeFrom?: { startIndex: number; existingResults: any[] }
  ) {
    const { rows, csvHeaders, mode, line1Col, postcodeCol, uprnCol } = input;
    const apiHeaders = buildApiHeaders(mode);
    const batchSize = mode === "uprn" ? UPRN_BATCH_SIZE : BATCH_SIZE;

    const startIndex = resumeFrom?.startIndex ?? 0;
    let currentResults: any[] = resumeFrom?.existingResults ?? [];

    if (resumeFrom) {
      setResultRows(currentResults);
      setProgress({ done: startIndex, total: rows.length });
    }

    setProcessing(true);
    setPaused(false);
    pausedRef.current = false;
    cancelledRef.current = false;
    setError(null);

    if (startIndex === 0) {
      setResultRows([]);
      setProgress({ done: 0, total: rows.length });
      currentResults = [];
    }

    const persist = (p: { done: number; total: number }) => {
      saveSnapshot({
        progress: p,
        resultRows: currentResults,
        originalRows: rows,
        csvHeaders,
        line1Col,
        postcodeCol,
      });
    };

    for (
      let batchStart = startIndex;
      batchStart < rows.length;
      batchStart += batchSize
    ) {
      if (cancelledRef.current) break;

      while (pausedRef.current) {
        if (cancelledRef.current) break;
        await new Promise((r) => setTimeout(r, PAUSE_POLL_MS));
      }
      if (cancelledRef.current) break;

      const batchEnd = Math.min(batchStart + batchSize, rows.length);
      const batch = rows.slice(batchStart, batchEnd);

      const batchPromises = batch.map(async (r, batchIndex) => {
        const originalIndex = batchStart + batchIndex;
        try {
          let res: CleanseResult;
          if (mode === "cleanse") {
            const queryStr = [r[line1Col], r[postcodeCol]]
              .filter((v) => !!v && String(v).trim().length > 0)
              .join(", ");
            res = await dataSource.cleanseAddress(queryStr);
          } else {
            const uprnValue = String(r[uprnCol] || "").trim();
            if (!uprnValue) throw new Error("Empty UPRN value");
            res = await dataSource.lookupByUprn(uprnValue);
          }
          return {
            success: true,
            rowOut: mapResultToRow(r, res, mode),
            originalIndex,
          };
        } catch (e: any) {
          // Credit exhaustion bubbles up so the batch handler can pause and
          // surface a top-up prompt; row-level errors are recorded inline.
          if (e?.code === "INSUFFICIENT_CREDITS" || e?.status === 402) throw e;
          return {
            success: false,
            rowOut: mapErrorToRow(r, e?.message || "Failed", apiHeaders),
            originalIndex,
          };
        }
      });

      try {
        const batchResults = await Promise.all(batchPromises);
        batchResults.sort((a, b) => a.originalIndex - b.originalIndex);

        batchResults.forEach(({ rowOut }) => {
          currentResults.push(rowOut);
          setResultRows((prev) => [...prev, rowOut]);
        });

        const newProgress = { done: batchEnd, total: rows.length };
        setProgress(newProgress);
        persist(newProgress);
      } catch (error: any) {
        console.error("Batch processing error:", error);

        if (error?.code === "INSUFFICIENT_CREDITS" || error?.status === 402) {
          setProcessing(false);
          setPaused(true);
          pausedRef.current = true;

          const currentProgress = { done: batchStart, total: rows.length };
          setProgress(currentProgress);
          persist(currentProgress);

          setError(
            "Insufficient API credits. Please top up your account and click Resume to continue processing."
          );
          return;
        }

        setError("Error processing batch");
      }
    }

    setProcessing(false);
    setPaused(false);
    pausedRef.current = false;

    if (!cancelledRef.current) clearSnapshot();
  }

  function cancel() {
    cancelledRef.current = true;
  }

  // Pre-load the visible state from a saved snapshot without starting the
  // loop. The composer calls this when the user clicks "Resume" — start() is
  // then called separately to pick up at the snapshot's progress index.
  function restore(snapshot: RecoverySnapshot) {
    setResultRows(snapshot.resultRows);
    setProgress(snapshot.progress);
  }

  function reset() {
    setProcessing(false);
    setPaused(false);
    cancelledRef.current = false;
    setProgress({ done: 0, total: 0 });
    setResultRows([]);
  }

  return {
    processing,
    paused,
    progress,
    resultRows,
    start,
    cancel,
    restore,
    reset,
  };
}
